<script setup lang="ts">
import { toRefs } from 'vue';

import type { Maybe } from '@nova/core';

import { useYardSpotPresenter, type YardSpot } from '@satellite/composables/useYardSpotPresenter';
import { type YardArea } from '@satellite/composables/useYardAreaPresenter';
import YardAreaPresenter from './YardAreaPresenter.vue';

const props = withDefaults(
  defineProps<{
    size?: 'regular' | 'small';
    spot?: Maybe<YardSpot>;
    spotId?: Maybe<string>;
    area?: Maybe<YardArea>;
  }>(),
  {
    size: 'regular',
    spot: null,
    spotId: null,
    area: null
  }
);

const { spotId, spot } = toRefs(props);

const { areaId, spotCode, isLoading, onIntersect } = useYardSpotPresenter({
  spotId,
  spot,
  immediate: false,
  live: true
});
</script>

<template>
  <v-skeleton-loader
    :loading="isLoading"
    type="text"
    v-intersect.once="{
      handler: onIntersect,
      options: {
        threshold: [0, 0.5, 1.0]
      }
    }">
    <div class="d-flex flex-row gap-1 align-center yard-spot-presenter" style="min-width: 50px">
      <yard-area-presenter :area="area" :area-id="areaId" :size="size" style="min-width: 50px" />
      -
      <span
        :class="size === 'small' ? 'typography-body-sm-bold' : 'typography-body-md-bold'"
        data-testid="yard-spot-presenter-spot-code">
        {{ spotCode }}
      </span>
    </div>
  </v-skeleton-loader>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  /** this value makes the loader have the same vertical spacing as the spot code text */
  margin: 6px 0 !important;
}
</style>
